import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  AlertCircle,
  TrendingUp,
  Phone,
  Video,
  Mail,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatDistanceToNow } from 'date-fns';

const INTERACTION_ICONS = {
  call: Phone,
  video_call: Video,
  meeting: Calendar,
  email: Mail,
  text: MessageSquare,
  social_media: MessageSquare,
  coffee: Calendar,
  event: Calendar,
  other: MessageSquare
};

function Dashboard() {
  const { fetchStats } = useApp();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      const data = await fetchStats();
      setStats(data);
      setLoading(false);
    };
    loadStats();
  }, []); // Only fetch once on mount

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="empty-state">
        <p>Unable to load dashboard data</p>
      </div>
    );
  }

  const getCategoryColor = (category) => {
    const colors = {
      family: '#ef4444',
      friends: '#22c55e',
      professional: '#3b82f6',
      acquaintance: '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
          Dashboard
        </h1>
        <p className="text-muted">
          Overview of your personal network
        </p>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats-grid">
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(99, 102, 241, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary)'
          }}>
            <Users size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.totalContacts}
            </div>
            <div className="text-sm text-muted">Total Contacts</div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(34, 197, 94, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--success)'
          }}>
            <MessageSquare size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.totalInteractions}
            </div>
            <div className="text-sm text-muted">Interactions</div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(245, 158, 11, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--warning)'
          }}>
            <AlertCircle size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.staleContacts?.length || 0}
            </div>
            <div className="text-sm text-muted">Need Attention</div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(139, 92, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--secondary)'
          }}>
            <TrendingUp size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.interactionsByType?.length || 0}
            </div>
            <div className="text-sm text-muted">Interaction Types</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content-grid">
        {/* Contacts by Category */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Contacts by Category</h2>
            <Link to="/contacts" className="btn btn-ghost btn-sm">
              View All <ChevronRight size={16} />
            </Link>
          </div>
          {stats.categoryCounts?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {stats.categoryCounts.map(item => (
                <div key={item.category} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: getCategoryColor(item.category)
                  }} />
                  <div style={{ flex: 1, textTransform: 'capitalize' }}>
                    {item.category}
                  </div>
                  <div className="font-semibold">{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No contacts yet</p>
              <Link to="/contacts" className="btn btn-primary btn-sm mt-3">
                Add Contacts
              </Link>
            </div>
          )}
        </div>

        {/* Contacts Needing Attention */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Need Attention</h2>
          </div>
          {stats.staleContacts?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {stats.staleContacts.map(contact => (
                <Link
                  key={contact.id}
                  to={`/contacts/${contact.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  className="hover-bg"
                >
                  <div className="avatar" style={{ width: '36px', height: '36px', fontSize: '0.75rem' }}>
                    {contact.first_name[0]}{contact.last_name?.[0] || ''}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="font-medium">
                      {contact.first_name} {contact.last_name}
                    </div>
                    <div className="text-xs text-muted">
                      {Math.round(contact.days_since_contact)} days since last contact
                    </div>
                  </div>
                  <span className="badge badge-warning">
                    Overdue
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <AlertCircle size={32} className="empty-state-icon" />
              <p>All contacts are up to date!</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Interactions */}
      <div className="card mt-4">
        <div className="card-header">
          <h2 className="card-title">Recent Interactions</h2>
        </div>
        {stats.recentInteractions?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {stats.recentInteractions.map(interaction => {
              const Icon = INTERACTION_ICONS[interaction.type] || MessageSquare;
              return (
                <div
                  key={interaction.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'var(--gray-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--gray-600)',
                    flexShrink: 0
                  }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Link
                        to={`/contacts/${interaction.contact_id}`}
                        className="font-medium"
                      >
                        {interaction.first_name} {interaction.last_name}
                      </Link>
                      <span className="text-sm text-muted">
                        {formatDistanceToNow(new Date(interaction.date), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="text-sm text-muted" style={{ textTransform: 'capitalize' }}>
                      {interaction.type.replace('_', ' ')}
                      {interaction.subject && ` - ${interaction.subject}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <MessageSquare size={32} className="empty-state-icon" />
            <p>No interactions logged yet</p>
          </div>
        )}
      </div>

      <style>{`
        .hover-bg:hover {
          background: var(--gray-50);
        }
        .dashboard-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .dashboard-content-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        @media (max-width: 1024px) {
          .dashboard-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 768px) {
          .dashboard-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }
          .dashboard-content-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 480px) {
          .dashboard-stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
